import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { t } from "@lingui/core/macro";

import { Button } from "_/components/button";
import { Icon } from "_/components/icon";
import { Modal, SubmitCancelButtons } from "_/components/modal";
import { testPermissions } from "_/components/permission-test";
import { ProgressBar } from "_/components/progress-bar";

import {
  type Command,
  type CommandKind,
  useExecuteCommand,
} from "_/data/commands";
import { type Machine, useMachine } from "_/data/machines";
import { useOrg } from "_/data/orgs";
import {
  type Attempt,
  type AttemptStatus,
  hasAttemptEnded,
  useMachineAttempts,
} from "_/data/projects";
import { ROOT_DOMAIN, useCurrentUserPermissions } from "_/data/rbac";
import { useIsAonUser } from "_/data/users";

import { type Uuid } from "_/types";

import * as S from "./styled";

const POLL_FREQUENCY_MSECS = 1_000;
const COMMAND_TIMEOUT_SECONDS = 30;
const ATTEMPT_STATUS_TIMEOUT_SECONDS = 30;

/**
 * Current stage of the stop or pause modal workflow.
 *
 * `running` tracks command execution, while `waitingForAttemptStatus`
 * confirms that a completed command produced the expected attempt status.
 */
type CommandModalState =
  // Waiting for the user to confirm the command.
  | "confirm"
  // Submitting the command or waiting for a terminal status.
  | "running"
  // The command completed and the attempt status confirms the action.
  | "completed"
  // Command completed, now waiting for the attempt status to change.
  | "waitingForAttemptStatus"
  // Command expired, execution timed out, or the attempt-status wait timed out.
  | "timeout"
  // The targeted print ended before submission or was replaced by another print.
  | "differentAttempt"
  // The command submission, polling, or execution failed.
  | "failed";

const isValidCommand = (kind: CommandKind, attempt: Attempt | undefined) =>
  attempt !== undefined &&
  (attempt.status === "printing" ||
    (attempt.status === "paused" && kind === "stop"));

const isWaitingForAttemptStatus = (kind: CommandKind, status: AttemptStatus) =>
  status === "printing" || (kind === "stop" && status === "paused");

function deriveModalState({
  hasSubmitted,
  action,
  command,
  currentAttempt,
  targetAttemptId,
  isError,
  attemptWaitElapsedSeconds,
}: {
  hasSubmitted: boolean;
  action: CommandKind;
  command: Command | undefined;
  isError: boolean;
  attemptWaitElapsedSeconds: number;
  /**
   * Attempt that's currently executing, which may be a later attempt than
   * `targetAttemptId`.
   *  */
  currentAttempt: Attempt | undefined;
  /** Attempt that we're trying to pause or stop. */
  targetAttemptId: Uuid | undefined;
}): CommandModalState {
  const canSubmitCommand =
    !hasSubmitted ||
    command?.status === "expired" ||
    command?.status === "timedOut";
  if (
    currentAttempt &&
    targetAttemptId !== undefined &&
    (currentAttempt.id !== targetAttemptId ||
      (canSubmitCommand && hasAttemptEnded(currentAttempt.status)))
  ) {
    return "differentAttempt";
  }

  if (!hasSubmitted) {
    return "confirm";
  }

  if (isError || command?.status === "failed") {
    return "failed";
  }

  if (command?.status === "expired" || command?.status === "timedOut") {
    return "timeout";
  }

  if (command?.status !== "completed") {
    return "running";
  }

  if (
    currentAttempt &&
    !isWaitingForAttemptStatus(action, currentAttempt.status)
  ) {
    return "completed";
  }

  return attemptWaitElapsedSeconds >= ATTEMPT_STATUS_TIMEOUT_SECONDS
    ? "timeout"
    : "waitingForAttemptStatus";
}

const CommandModalContent = ({
  action,
  state,
  command,
  commandElapsedSeconds,
  attemptWaitElapsedSeconds,
  onClose,
  onSubmitCommand,
}: {
  action: CommandKind;
  state: CommandModalState;
  command: Command | undefined;
  commandElapsedSeconds: number;
  attemptWaitElapsedSeconds: number;
  onClose: () => void;
  onSubmitCommand: () => void;
}): ReactNode => {
  const isStop = action === "stop";

  // Divide progress between the "running" and "waitingForAttemptStatus" states.
  // Move progress quickly for the initial phase, then slow it down to avoid the
  // UI looking hung.
  //
  // If `modalState` is "running":
  // - First 50% should be COMMAND_TIMEOUT_SECONDS.
  // - After that, fill the next 20% at an exponentially-decreasing rate.
  //
  // If `modalState` is "waitingForAttemptStatus", progress starts at 70%, and
  // divides ATTEMPT_STATUS_TIMEOUT_SECONDS into the remaining 30%.
  const commandProgress =
    commandElapsedSeconds <= COMMAND_TIMEOUT_SECONDS
      ? (commandElapsedSeconds / COMMAND_TIMEOUT_SECONDS) * 0.5
      : 0.7 -
        0.2 * (19 / 20) ** (commandElapsedSeconds - COMMAND_TIMEOUT_SECONDS);
  const waitingProgress = Math.min(
    0.7 + (attemptWaitElapsedSeconds / ATTEMPT_STATUS_TIMEOUT_SECONDS) * 0.3,
    1
  );

  const closeButtonLabel = t`common.close`;
  const statusMessages: Record<string, string> = {
    pending: t`components.stop-pause-buttons.sending-to-machine`,
    sent: t`components.stop-pause-buttons.sent-to-machine`,
    delivered: t`components.stop-pause-buttons.executing-on-machine`,
    submitting: t`components.stop-pause-buttons.submitting`,
  };
  const runningMessage =
    statusMessages[command?.status ?? "submitting"] ?? command?.status ?? "";
  const stateConfig = {
    confirm: {
      progress: 0,
      submitLabel: isStop
        ? t`components.stop-pause-buttons.submit-stop-request`
        : t`components.stop-pause-buttons.submit-pause-request`,
      content: isStop
        ? t`components.stop-pause-buttons.confirm-stop`
        : t`components.stop-pause-buttons.confirm-pause`,
    },
    running: {
      progress: commandProgress,
      submitLabel: closeButtonLabel,
      content: runningMessage,
    },
    waitingForAttemptStatus: {
      progress: waitingProgress,
      submitLabel: closeButtonLabel,
      content: t`components.stop-pause-buttons.waiting-for-confirmation`,
    },
    completed: {
      progress: 1,
      submitLabel: closeButtonLabel,
      content: isStop
        ? t`components.stop-pause-buttons.stopped`
        : t`components.stop-pause-buttons.paused`,
    },
    differentAttempt: {
      progress: 1,
      submitLabel: closeButtonLabel,
      content: isStop
        ? t`components.stop-pause-buttons.different-attempt-stop`
        : t`components.stop-pause-buttons.different-attempt-pause`,
    },
    timeout: {
      progress: 1,
      submitLabel: isStop
        ? t`components.stop-pause-buttons.retry-stop`
        : t`components.stop-pause-buttons.retry-pause`,
      content:
        command?.status === "expired"
          ? t`components.stop-pause-buttons.expired`
          : t`components.stop-pause-buttons.timeout`,
    },
    failed: {
      progress: 1,
      submitLabel: closeButtonLabel,
      content: t`components.stop-pause-buttons.failed`,
    },
  };
  const { progress, submitLabel, content } = stateConfig[state];
  const canSubmitCommand = state === "confirm" || state === "timeout";

  return (
    <S.ModalContent>
      {state !== "confirm" && (
        <ProgressBar
          value={progress}
          kind={state === "failed" || state === "timeout" ? "danger" : "info"}
        />
      )}
      <p>{content}</p>
      {command?.error && (
        <p>
          {t`components.stop-pause-buttons.support-error`}: {command.error}
        </p>
      )}

      <SubmitCancelButtons
        submitButtonLabel={submitLabel}
        disableSubmit={false}
        onCancel={canSubmitCommand ? onClose : undefined}
        onSubmit={canSubmitCommand ? onSubmitCommand : onClose}
      />
    </S.ModalContent>
  );
};

type StopPauseButtonsProps = {
  machine: Machine;
  className?: string;
  onModalOpenChange?: (isOpen: boolean) => void;
} & (
  | {
      attempt?: never;
      refreshAttempt?: never;
    }
  | {
      attempt: Attempt;
      refreshAttempt: () => Promise<void>;
    }
);

export const StopPauseButtons = ({
  machine,
  attempt: providedAttempt,
  className,
  refreshAttempt: refreshProvidedAttempt,
  onModalOpenChange,
}: StopPauseButtonsProps) => {
  const isAonUser = useIsAonUser();
  const { data: org } = useOrg(machine.orgId);
  const { data: permissions } = useCurrentUserPermissions(
    org?.domainId ?? ROOT_DOMAIN
  );
  const hasCommandPermissions = testPermissions(
    ["commands/create", "commands/poll", "commands/read"],
    permissions
  );
  const { id: machineId, status: machineStatus } = machine;

  const { data: attempts, refetch: refetchAttempts } = useMachineAttempts(
    providedAttempt ? undefined : machine
  );
  const { refetch: refetchMachine } = useMachine(machineId, { enabled: false });
  const attempt = providedAttempt ?? attempts?.[0];
  const previousAttemptRef = useRef(attempt);

  // If attempt status changes, refetch the machine status.
  useEffect(() => {
    if (!attempt) {
      return;
    }

    const previousAttempt = previousAttemptRef.current;
    const attemptStatusChanged =
      previousAttempt?.id === attempt.id &&
      previousAttempt?.status !== attempt.status;
    previousAttemptRef.current = attempt;

    if (attemptStatusChanged) {
      void refetchMachine();
    }
  }, [attempt, refetchMachine]);

  const refreshAttempt = useCallback(async () => {
    if (refreshProvidedAttempt) {
      await refreshProvidedAttempt();
    } else {
      await refetchAttempts();
    }
  }, [refreshProvidedAttempt, refetchAttempts]);

  useEffect(() => {
    if (!providedAttempt) {
      void refetchAttempts();
    }
  }, [machineId, machineStatus, refetchAttempts, providedAttempt]);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [action, setAction] = useState<CommandKind>("stop");
  const [targetAttemptId, setTargetAttemptId] = useState<Uuid>();
  const [hasSubmitted, setHasSubmitted] = useState(false);
  const [commandElapsedSeconds, setCommandElapsedSeconds] = useState(0);
  const [attemptWaitElapsedSeconds, setAttemptWaitElapsedSeconds] = useState(0);
  const {
    send,
    command,
    isExecuting,
    isError,
    reset: resetCommand,
  } = useExecuteCommand(POLL_FREQUENCY_MSECS);
  const modalState = deriveModalState({
    hasSubmitted,
    action,
    command,
    currentAttempt: attempt,
    targetAttemptId,
    isError,
    attemptWaitElapsedSeconds,
  });

  // Timer for setting the progress bar
  useEffect(() => {
    if (modalState !== "running" && modalState !== "waitingForAttemptStatus") {
      return undefined;
    }

    const updateElapsedSeconds =
      modalState === "running"
        ? setCommandElapsedSeconds
        : setAttemptWaitElapsedSeconds;
    const intervalId = window.setInterval(() => {
      updateElapsedSeconds((current) => current + POLL_FREQUENCY_MSECS / 1000);
    }, POLL_FREQUENCY_MSECS);

    return () => window.clearInterval(intervalId);
  }, [modalState]);

  // Timer for polling the attempt status while waiting for it to change
  useEffect(() => {
    if (modalState !== "waitingForAttemptStatus") {
      return undefined;
    }

    let isActive = true;
    const pollOnce = () => {
      return window.setTimeout(() => void pollAttempt(), POLL_FREQUENCY_MSECS);
    };
    async function pollAttempt() {
      await refreshAttempt();

      if (isActive) {
        timeoutId = pollOnce();
      }
    }

    let timeoutId = pollOnce();

    return () => {
      isActive = false;
      window.clearTimeout(timeoutId);
    };
  }, [modalState, refreshAttempt]);

  // TODO: remove the isAonUser check after rollout of the Nov 2026 machine
  // software release.
  if (!isAonUser || !hasCommandPermissions || !attempt) {
    return null;
  }

  const attemptIsControllable =
    attempt.status === "printing" || attempt.status === "paused";
  const isBusy =
    modalState === "running" || modalState === "waitingForAttemptStatus";

  if (!attemptIsControllable && !isModalOpen && !isBusy) {
    return null;
  }

  const openCommandModal = (nextAction: CommandKind) => {
    if (isBusy) {
      return;
    }

    resetCommand();
    setAction(nextAction);
    setTargetAttemptId(attempt.id);
    setHasSubmitted(false);
    setCommandElapsedSeconds(0);
    setAttemptWaitElapsedSeconds(0);
    setIsModalOpen(true);
    onModalOpenChange?.(true);
  };

  const closeCommandModal = () => {
    setIsModalOpen(false);
    onModalOpenChange?.(false);
    if (!isBusy) {
      setTargetAttemptId(undefined);
      setHasSubmitted(false);
      resetCommand();
    }
  };

  const submitCommand = () => {
    if (attempt.id !== targetAttemptId) {
      return;
    }

    if (!isValidCommand(action, attempt)) {
      closeCommandModal();
      return;
    }

    resetCommand();
    setCommandElapsedSeconds(0);
    setAttemptWaitElapsedSeconds(0);
    setHasSubmitted(true);
    send({
      action: { kind: action, payload: { attemptId: targetAttemptId } },
      machineId: attempt.machineId,
    });
  };

  return (
    <S.Container className={className}>
      {attemptIsControllable && (
        <>
          <Button
            title={t`common.pause`}
            variant="contained"
            kind="warning"
            size="large"
            disabled={attempt.status === "paused" || isBusy || isExecuting}
            iconOnly
            onClick={() => openCommandModal("pause")}
          >
            <Icon variant="Pause" />
          </Button>
          <Button
            title={t`common.stop`}
            variant="contained"
            kind="danger"
            size="large"
            disabled={isBusy || isExecuting}
            iconOnly
            onClick={() => openCommandModal("stop")}
          >
            <Icon variant="Stop" />
          </Button>
        </>
      )}
      {isModalOpen && (
        <Modal
          title={action === "stop" ? t`common.stop` : t`common.pause`}
          onClose={closeCommandModal}
        >
          <CommandModalContent
            action={action}
            state={modalState}
            command={command}
            commandElapsedSeconds={commandElapsedSeconds}
            attemptWaitElapsedSeconds={attemptWaitElapsedSeconds}
            onClose={closeCommandModal}
            onSubmitCommand={submitCommand}
          />
        </Modal>
      )}
    </S.Container>
  );
};
